import React, { useRef, useState, useEffect } from 'react';
import './Slider.css';
const steps = [0, 25, 50, 75, 100];
const clamp = value => Math.max(0, Math.min(100, Number(value) || 0));
export default function SliderQuantity({ value, onSliderChange, direction, stateUpdate }) {
  const track = useRef(null), gesture = useRef(null);
  const [draft, setDraft] = useState(null);
  const frame = useRef(null), pending = useRef(0);
  const callbacks = useRef({ onSliderChange, stateUpdate });
  callbacks.current = { onSliderChange, stateUpdate };
  const cancelFrame = () => { if (frame.current !== null) cancelAnimationFrame(frame.current); frame.current = null; };
  useEffect(() => () => cancelFrame(), []);
  const updateLive = next => {
    setDraft(next);
    pending.current = next;
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      callbacks.current.onSliderChange?.(pending.current);
      callbacks.current.stateUpdate?.(false);
    });
  };
  const rawValue = Number(value) || 0;
  const overLimit = draft === null && rawValue > 100;
  const shown = draft === null ? clamp(rawValue) : draft;
  const displayPercent = overLimit ? rawValue : shown;
  const commit = next => { cancelFrame(); onSliderChange?.(next); stateUpdate?.(false); setDraft(null); };
  const position = event => {
    const rect = track.current.getBoundingClientRect();
    const raw = clamp((event.clientX - rect.left - (gesture.current?.offset || 0)) / rect.width * 100);
    const nearest = Math.round(raw / 25) * 25;
    return Math.abs(raw - nearest) <= 0.5 ? nearest : raw;
  };
  return <div className={'slider-container ' + (direction === 'Sell' ? 'sell' : 'buy') + (overLimit ? ' slider-over' : '')}>
    <div className="slider-track" ref={track} role="slider" tabIndex={0} aria-label="Order size percentage" aria-valuemin={0} aria-valuemax={100} aria-valuenow={shown}
      onKeyDown={event => {
        const key = event.key;
        if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(key)) return;
        event.preventDefault();
        commit(key === 'Home' ? 0 : key === 'End' ? 100 : clamp(shown + (key === 'ArrowRight' || key === 'ArrowUp' ? 1 : -1)));
      }}
      onPointerDown={event => {
        if (event.button !== 0 || event.target.closest('button')) return;
        event.preventDefault();
        const rect = track.current.getBoundingClientRect();
        gesture.current = { offset: event.target.closest('.slider-thumb') ? event.clientX - rect.left - shown / 100 * rect.width : 0 };
        stateUpdate?.(false);
        event.currentTarget.setPointerCapture(event.pointerId); updateLive(position(event));
      }}
      onPointerMove={event => { if (gesture.current !== null) updateLive(position(event)); }}
      onPointerUp={event => {
        if (gesture.current === null) return;
        const next = position(event); gesture.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId); commit(next);
      }}
      onPointerCancel={() => { cancelFrame(); gesture.current = null; setDraft(null); }}>
      <div className="slider-fill" style={{ width: shown + '%', backgroundColor: overLimit ? 'var(--slider-over, #f5a623)' : 'var(--slider-fill, #e8eaf0)' }} />
      {steps.map(step => <React.Fragment key={step}>
        <button type="button" className="slider-percent-label" style={{ left: step + '%', opacity: 1 }} onClick={() => commit(step)}>{step}%</button>
        <div className={'slider-step ' + (shown >= step ? 'active' : '')} style={{ left: step + '%' }} />
      </React.Fragment>)}
      {(overLimit || !steps.some(step => Math.abs(shown - step) < 0.01)) && (
        <div className="slider-value slider-live-percentage" style={{ left: shown + '%' }}>
          {displayPercent.toFixed(2)}%
        </div>
      )}
      <div className="slider-thumb" style={{ left: shown + '%' }} />
    </div>
  </div>;
}
